/* eslint-disable @typescript-eslint/no-unsafe-call */
import { useQueryClient } from '@tanstack/react-query';
import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { useModal } from '../../../application/hooks/useModal';
import { logger } from '../../../application/logging/logging';
import { generateIssueReport } from '../../../features/report';
import { issueReportFileNameTemplate } from '../../../features/report/issues/issueReport.constants';
import { triggerDownloadFileDialog } from '../../../features/report/report-helpers';
import {
  PerformIssueOperationDto,
  IssueOperationStateEnum,
  IIssueOperationDto,
  PatientStudyStateEnum,
  StudyRoleClaimValue,
} from '../../../services/api/api-client';
import { showServerErrorToast } from '../../toast/toast-helper';
import { IssueQueryParamsType } from '../bar/issueFilters-helper';
import { IssueDeleteDialog, IssueDeleteFormParams } from '../deleteDialog/issueDeleteDialog.component';
import { IssueForm, IssueCreationTypeWithId } from '../form/issueForm.component';
import { useGenerateReportForm, UseFormGenerateReportType } from '../generateReportForm/generateReportForm.component';
import { IssueRejectForm, IssueRejectFormParams, UseFormRejectIssueType } from '../rejectForm/rejectForm.component';
import {
  IssueResolveForm,
  IssueResolveFormParams,
  UseFormResolveIssueType,
} from '../resolveForm/resolveIssueForm.component';
import { IssueRevokeDialog, IssueRevokeDialogParams } from '../revokeDialog/issueRevokeDialog.component';
import { IssueViewModal } from '../view/issueView.component';
import { IssuesContext, IssuesContextType } from './issues.context';
import {
  getIssueHistoryQueryKey,
  getIssueQueryKey,
  getIssuesForStudyQueryKey,
  getIssuesGroupedByPatientsQueryKey,
  useGetIssuesForStudyQuery,
} from 'src/services/api/api-client/IssueQuery';
import { patientFilterParams } from 'src/components/patientTable/patientFilters.helpers';
import { useQueryParams, NumberParam } from 'use-query-params';
import {
  getIssue,
  getIssuesForStudy,
  performIssueOperation,
  removeIssue,
  removeIssueOperation,
  resumeIssueOperation,
} from 'src/services/api/api-client/IssueClient';
import { useFormEditingAndOverview } from 'src/features/forms/useFormEditingAndOverview';
import { useNotesOverview } from 'src/components/expandedRow/notesSection/overview/useNotesOverview';
import { getPatientByStudyAndUniqId } from 'src/services/api/api-client/PatientClient';
import { useLocalStorage } from 'src/helpers/useLocalStorage';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { useHasStudyClaims } from 'src/helpers/auth/auth-helper';
import { getPatientsQueryKey } from 'src/services/api/api-client/PatientQuery';
import { getDoctorProfileInfoQueryKey } from 'src/services/api/api-client/ProfileQuery';
import {
  getNotificationBatchesQueryKey,
  getNotificationCountsQueryKey,
  getNotificationsQueryKey,
} from 'src/services/api/api-client/NotificationQuery';

export const IssuesProvider: FC<PropsWithChildren<IssuesContextType>> = ({ children }) => {
  const queryClient = useQueryClient();
  const study = useStudy();
  const [loadedPatientsUid, setLoadedPatientsUid] = useState<string[]>();
  const { hasStudyClaim } = useHasStudyClaims();
  const [isIssuesVisible, setIssuesVisible] = useLocalStorage<boolean>(
    'isIssuesVisible',
    !hasStudyClaim(StudyRoleClaimValue.PatientCreate),
  );

  const { modal: generateReportModal, open: openGenerateReport } = useGenerateReportForm();

  const { data: patientIssues } = useGetIssuesForStudyQuery(
    {
      studyId: study?.id ?? 0,
      subjectList: ['Patient'],
      patientUniqIds: loadedPatientsUid,
      limit: 9999,
    },
    {
      enabled: !!study && study.hasMonitoring && hasStudyClaim(StudyRoleClaimValue.IssueView),
      suspense: false,
    },
  );

  const { data: studyIssues } = useGetIssuesForStudyQuery(
    {
      studyId: study?.id ?? 0,
      subjectList: ['Study'],
      limit: 9999,
    },
    {
      enabled: !!study && study.hasMonitoring && hasStudyClaim(StudyRoleClaimValue.IssueView),
      suspense: false,
    },
  );

  const issues = useMemo(
    () => [...(patientIssues?.data || []), ...(studyIssues?.data || [])],
    [patientIssues?.data, studyIssues?.data],
  );

  const [, setQueryParams] = useQueryParams({
    ...patientFilterParams(),
    issueId: NumberParam,
  });

  const createIssueModal = useModal<Partial<IssueCreationTypeWithId> | undefined>();
  const deleteModal = useModal<IssueDeleteFormParams>();
  const resolveIssueModal = useModal<IssueResolveFormParams>();
  const rejectIssueModal = useModal<IssueRejectFormParams>();
  const issueRevokeDialogModal = useModal<IssueRevokeDialogParams>();
  const issueViewModal = useModal<number>();

  const formEditingAndOverview = useFormEditingAndOverview();
  const notesOverview = useNotesOverview();

  const [processingIssueId, setProcessingIssueId] = useState<number | undefined>();

  const invalidateQueries = useCallback(
    async (issueId: number) => {
      if (!study) return;

      await Promise.all([
        queryClient.invalidateQueries(getPatientsQueryKey()),
        queryClient.invalidateQueries(getDoctorProfileInfoQueryKey()),
        queryClient.invalidateQueries(getIssueQueryKey(issueId)),
        queryClient.invalidateQueries(getIssueHistoryQueryKey(issueId)),
        queryClient.invalidateQueries(getIssuesForStudyQueryKey(study.id)),
        queryClient.invalidateQueries(getIssuesGroupedByPatientsQueryKey(study.id)),
        queryClient.invalidateQueries(getNotificationCountsQueryKey(study.id)),
        queryClient.invalidateQueries(getNotificationBatchesQueryKey(undefined, [study.id])),
      ]);
    },
    [queryClient, study],
  );

  const resumeOperationHandler = useCallback(
    async (issueOperation: IIssueOperationDto, newState: IssueOperationStateEnum, data?: UseFormRejectIssueType) => {
      setProcessingIssueId(issueOperation.issueId);
      try {
        await resumeIssueOperation(issueOperation.id, newState, data?.comment);
        await invalidateQueries(issueOperation.issueId);
      } catch (err: any) {
        logger().error(err);
        showServerErrorToast(err);
      } finally {
        setProcessingIssueId(undefined);
      }
    },
    [invalidateQueries],
  );

  const deleteIssue = useCallback(
    (issueId: number) => {
      const callback = async () => {
        if (!study) return;
        try {
          await removeIssue(issueId);

          await Promise.all([
            queryClient.invalidateQueries(getDoctorProfileInfoQueryKey()),
            queryClient.invalidateQueries(getPatientsQueryKey()),
            queryClient.invalidateQueries(getIssuesForStudyQueryKey(study.id)),
            queryClient.invalidateQueries(getNotificationsQueryKey(undefined, study.id)),
            queryClient.invalidateQueries(getNotificationCountsQueryKey(study.id)),
            queryClient.invalidateQueries(getNotificationBatchesQueryKey(undefined, [study.id])),
          ]);

          deleteModal.closeModal();
          issueViewModal.closeModal();
        } catch (err: any) {
          logger().error(err);
          showServerErrorToast(err);
        }
      };

      deleteModal.openModal({ issueId, callback });
    },
    [deleteModal, issueViewModal, queryClient, study],
  );

  const performOperation = useCallback(
    (issueId: number) => {
      const callback = async (data: UseFormResolveIssueType) => {
        setProcessingIssueId(issueId);
        try {
          await performIssueOperation(issueId, new PerformIssueOperationDto({ ...data }));
          await invalidateQueries(issueId);
        } catch (err: any) {
          logger().error(err);
          showServerErrorToast(err);
        } finally {
          setProcessingIssueId(undefined);
        }
      };

      resolveIssueModal.openModal({ issueId, callback });
    },
    [invalidateQueries, resolveIssueModal],
  );

  const revokeOperation = useCallback(
    async (issueOperation: IIssueOperationDto) => {
      const callback = async () => {
        setProcessingIssueId(issueOperation.issueId);
        try {
          await removeIssueOperation(issueOperation.id);
          await invalidateQueries(issueOperation.issueId);
        } catch (err: any) {
          logger().error(err);
          showServerErrorToast(err);
        } finally {
          setProcessingIssueId(undefined);
        }
      };

      issueRevokeDialogModal.openModal({ issueId: issueOperation.issueId, callback });
    },
    [invalidateQueries, issueRevokeDialogModal],
  );

  const approveOperation = useCallback(
    (issueOperation: IIssueOperationDto) => {
      resumeOperationHandler(issueOperation, IssueOperationStateEnum.Approved);
    },
    [resumeOperationHandler],
  );

  const rejectOperation = useCallback(
    (issueOperation: IIssueOperationDto) => {
      const callback = (data: UseFormRejectIssueType) =>
        resumeOperationHandler(issueOperation, IssueOperationStateEnum.Rejected, data);

      rejectIssueModal.openModal({ issueId: issueOperation.issueId, callback });
    },
    [rejectIssueModal, resumeOperationHandler],
  );

  const generateReport = useCallback(
    async (queryParams?: IssueQueryParamsType): Promise<void> => {
      const callback = async (data: UseFormGenerateReportType) => {
        if (!study) return;

        const response = await getIssuesForStudy(
          study.id,
          queryParams?.patientUid ? queryParams.patientUid : undefined,
          undefined,
          queryParams?.state,
          queryParams?.subjectList,
          queryParams?.patientGroupList,
          queryParams?.unreadOnly ? true : undefined,
          0,
          9999,
          'id',
        );

        const blob = await generateIssueReport({
          data: response.data,
          studyNumber: study.studyNumber,
          studyName: study.title,
          filters: queryParams,
          studyGroups: study.groups,
          furtherInfo: data.furtherInfo,
        });

        triggerDownloadFileDialog(blob, issueReportFileNameTemplate(study.studyNumber));
      };

      await openGenerateReport(callback);
    },
    [openGenerateReport, study],
  );

  const navigateToIssue = useCallback(
    async (issueId: number) => {
      const issue = await getIssue(issueId);
      const patient = issue?.linkedPatientUniqId
        ? await getPatientByStudyAndUniqId(issue.studyId, issue.linkedPatientUniqId)
        : undefined;

      setQueryParams(
        {
          issueId: issueId,
          searchQuery: issue?.linkedPatientUniqId,
          patientGroup: patient?.group,
          studyState: !patient?.isStudyStarted
            ? PatientStudyStateEnum.NotStarted
            : patient?.isStudyFinished && !patient.archivedAt
            ? PatientStudyStateEnum.Finished
            : patient.archivedAt
            ? PatientStudyStateEnum.Archived
            : PatientStudyStateEnum.InProgress,
        },
        'replace',
      );

      if (issue.topic === 'Records' && issue.resultId && issue.fieldId && patient?.id) {
        formEditingAndOverview.openOverview({
          formResultId: issue.resultId,
          navigateToNodeId: issue.fieldId,
        });
      }

      const patientsGroup = study?.groups?.find((g) => g.id === patient?.group);
      if (issue.topic === 'Notes' && issue.resultId && patientsGroup?.formConfigIdForNotes) {
        notesOverview.open({
          patientId: patient!.id,
          resultId: issue.resultId,
          configId: patientsGroup.formConfigIdForNotes,
        });
      }

      // TODO: navigate to survey result
      // if (issue.topic === 'Surveys' && issue.resultId) {
      // }
    },
    [formEditingAndOverview, notesOverview, setQueryParams, study?.groups],
  );

  return (
    <IssuesContext.Provider
      value={{
        processingIssueId,

        createIssue: createIssueModal.openModal,
        editIssue: createIssueModal.openModal,
        openIssueView: issueViewModal.openModal,

        deleteIssue,
        performOperation,
        rejectOperation,
        approveOperation,
        revokeOperation,
        generateReport,

        setLoadedPatientsUid,
        loadedIssues: issues,

        isIssuesVisible,
        setIssuesVisible,
        navigateToIssue,
      }}
    >
      {children}

      {study?.hasMonitoring && (
        <>
          {study.id && createIssueModal.visible && <IssueForm modal={createIssueModal} studyId={study.id} />}
          <IssueViewModal modal={issueViewModal} />
          <IssueRejectForm modal={rejectIssueModal} />
          <IssueDeleteDialog modal={deleteModal} />
          <IssueResolveForm modal={resolveIssueModal} />
          <IssueRevokeDialog modal={issueRevokeDialogModal} />
          {generateReportModal}
        </>
      )}
      {formEditingAndOverview.element}
      {notesOverview.element}
    </IssuesContext.Provider>
  );
};
